import {NavController} from '@ionic/angular';
import {ActivatedRoute, ParamMap, Params, Router} from '@angular/router';
import {RouteUtils} from '../utils/route.utils';
import {ToastControl} from '../../../controls/core/toast/toast.control';
import {AppInjector} from '../../../app-injector';
import {NavigationOptions} from '@ionic/angular/providers/nav-controller';
import {PlatformControl} from '../../../controls/core/platform/platform.control';
import {Location} from '@angular/common';
import {CommandAction} from '../command/commandAction';
import {ChangeDetectorRef, Input} from '@angular/core';

export abstract class BaseView {
    protected navController: NavController;
    protected routeUtils: RouteUtils;
    protected toastControl: ToastControl;
    public platformControl: PlatformControl;
    protected location: Location;
    private pRouter: Router;

    // Don't expose this to the child. I think we can only subscribe once to the observable
    private internalActivatedRoute: ActivatedRoute;

    private returnUrlFromUrl;

    protected constructor() {
        const injector = AppInjector.injector;
        this.navController = injector.get(NavController);
        this.routeUtils = injector.get(RouteUtils);
        this.toastControl = injector.get(ToastControl);
        this.platformControl = injector.get(PlatformControl);
        this.internalActivatedRoute = injector.get(ActivatedRoute);
        this.internalActivatedRoute.queryParamMap.subscribe(value => this.returnUrlFromUrl = this.routeUtils.getRedirectUrl(value));
        this.location = injector.get(Location);
        this.pRouter = injector.get(Router);
    }

    get currentPath(): string {
        let path = window.location?.hash;
        if (path && path.length > 0) {
            if (path[0] === '#') {
                path = path.substring(1);
            }
        }

        return path;
    }

    public redirectToFromCommandAction(commandAction: CommandAction) {
        if (commandAction.withInApp) {
            this.redirectToWithinCurrentApp(commandAction.url, commandAction.queryParams);
        } else {
            this.redirectTo(commandAction.url, commandAction.queryParams);
        }
    }

    public redirectToWithinCurrentApp(url: string, queryParams?: Params, redirectUrl?: string, navigationOptions?: NavigationOptions) {
        const fullUrl = this.getUrlWithinApp(url);
        console.log(['redirectToWithinCurrentApp', window.location, url, fullUrl]);
        if (fullUrl) {
            this.redirectTo(fullUrl, queryParams, redirectUrl, navigationOptions);
        }
    }

    public redirectToWithinCurrentAppInNewTab(url: string, queryParams?: Params) {
        const fullUrl = this.getUrlWithinApp(url);
        if (fullUrl) {
            this.redirectToNewTab(fullUrl, queryParams);
        }
    }

    private getUrlWithinApp(url: string): string | undefined {
        if (!window.location.hash) return undefined;

        // hash attendu: "#/app/..." (HashLocationStrategy)
        const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
        const hashPath = hash.startsWith('/') ? hash.slice(1) : hash; // "app/..."
        const parts = hashPath.split('/').filter(Boolean);

        // parts[0] = "app" (ou ton segment racine)
        const app = parts[0];
        if (!app) return undefined;

        const cleanUrl = url.startsWith('/') ? url.slice(1) : url;

        // Empêche "/app/app/..." si l'appelant passe déjà "app/xxx"
        if (cleanUrl.startsWith(app + '/')) {
            return `/${cleanUrl}`;
        }

        return `/${app}/${cleanUrl}`;
    }

    public redirectToNewTab(url: string, queryParams?: Params) {
        const tree = this.pRouter.createUrlTree([url], { queryParams });
        const serialized = this.pRouter.serializeUrl(tree);   // ex: "/app/page?x=1"
        const external = this.location.prepareExternalUrl(serialized); // ex: "/#/app/page?x=1"
        window.open(`${window.location.origin}${external}`, '_blank');
    }

    public redirectTo(url: string, queryParams?: Params, redirectUrl?: string, navigationOptions?: NavigationOptions) {
        console.log('redirectTo', [url, queryParams, redirectUrl, navigationOptions]);
        const extraOptionQueryParams: Params = queryParams ? queryParams : {};
        const queryParamMap: ParamMap = this.pRouter.parseUrl(url).queryParamMap;
        queryParamMap?.keys.forEach(key => extraOptionQueryParams[key] = queryParamMap.get(key));
        redirectUrl = redirectUrl ? redirectUrl : this.currentPath;
        if (redirectUrl) {
            extraOptionQueryParams['returnUrl'] = redirectUrl;
        }
        if (!navigationOptions) {
            navigationOptions = {};
        }

        navigationOptions.queryParams = extraOptionQueryParams;
        console.log('Final redirection ' + url);
        this.navController.navigateForward(url, navigationOptions);
    }

    protected isDesktop(): boolean {
        return this.platformControl.isAtLeast(PlatformControl.LG);
    }

    protected redirectToBackMode(url: string) {
        this.navController.navigateBack(url);
    }

    protected extractReturnUrlFormUrl(): string {
        return this.returnUrlFromUrl;
    }

    protected back() {
        if (this.returnUrlFromUrl) {
            this.redirectToBackMode(this.returnUrlFromUrl);
        } else {
            this.navController.pop();
        }
    }
}
